import { Report, Reporter } from "@vonage/js-onewebrtc-telemetry";
import { version } from "../../../package.json";
import { BackgroundOptions, MediaProcessorConfig } from "../../main";
import { BackgroundFilter } from "../filters/background-filter";
import { SelfieSegmentationInterface, MediapipeSelfieSegmentation, LightWeightSelfieSegmentation } from "../wasm/selfie-segmentation/wrapper";
import { ResolvedWebglQuery } from "../renderers/webgl/webgl-profiler";

export class BackgroundTransformer {
    private selfieSegmentation?: SelfieSegmentationInterface;
    private backgroundFilter?: BackgroundFilter;
    private backgroundOptions?: BackgroundOptions;
    private reporter = new Reporter();
    private isEnabled = true;
    private avoidOriginalTrackLeak? = false;

    public async init(id: string, config: MediaProcessorConfig) {
        if (config.segmentationOptions?.lightWeightEngine === true) {
            this.selfieSegmentation = new LightWeightSelfieSegmentation();
        } else {
            this.selfieSegmentation = new MediapipeSelfieSegmentation();
        }
        this.selfieSegmentation?.init(
            config.modelAssetUriPath,
            config.mediapipeBaseAssetsUri,
            config.segmentationOptions?.gpuSupport
        );
        this.avoidOriginalTrackLeak = config.avoidOriginalTrackLeak;

        this.backgroundFilter = new BackgroundFilter();
        this.report({
            variation: "Create",
        });
    }

    public async transform?(
        frame: VideoFrame,
        controller: TransformStreamDefaultController
    ) {
        if (!this.isEnabled) {
            return controller.enqueue(frame);
        }

        let canvas;
        const timestamp = frame.timestamp ?? 0;

        // Detect frame rotation. On some browsers (Safari mobile in portrait),
        // MediaStreamTrackProcessor in a worker produces frames with raw sensor
        // orientation (landscape) while displayWidth/displayHeight reflect the
        // intended portrait dimensions. We detect this by comparing coded vs
        // display dimensions.
        const rotation = this.detectRotation(frame);

        const input = rotation !== 0
            ? await this.createRotatedBitmap(frame, rotation)
            : await createImageBitmap(frame);

        const mask = await this.selfieSegmentation?.process(input);
        if (mask) {
            canvas = await this.backgroundFilter?.process(input, mask);
        }
        if (!canvas && this.avoidOriginalTrackLeak) {
            canvas = new OffscreenCanvas(input.width, input.height);
        }
        if (canvas) {
            frame.close();
            controller.enqueue(
                new VideoFrame(canvas as any as HTMLCanvasElement, {
                    timestamp,
                    alpha: "discard",
                })
            );
        } else {
            controller.enqueue(frame);
        }
    }

    /**
     * Detects if a VideoFrame needs rotation by comparing its coded dimensions
     * against its display dimensions, or by reading the rotation property when
     * available (Chrome 138+).
     *
     * Returns 0, 90, 180, or 270 (clockwise degrees).
     */
    private detectRotation(frame: VideoFrame): number {
        // Prefer the explicit rotation property if supported
        if ('rotation' in frame && (frame as any).rotation !== 0) {
            return (frame as any).rotation as number;
        }

        // Fallback: infer rotation from dimension mismatch.
        // If codedWidth > codedHeight but displayWidth < displayHeight (or vice versa),
        // the frame has 90° or 270° rotation that hasn't been applied to the pixels.
        const cw = frame.codedWidth;
        const ch = frame.codedHeight;
        const dw = frame.displayWidth;
        const dh = frame.displayHeight;

        if (cw && ch && dw && dh) {
            const codedLandscape = cw > ch;
            const displayLandscape = dw > dh;

            if (codedLandscape !== displayLandscape) {
                // Dimensions are swapped — 90° rotation
                return 90;
            }
        }

        return 0;
    }

    /**
     * Creates an ImageBitmap from a rotated VideoFrame by drawing the raw frame
     * onto a canvas with the appropriate rotation applied, producing correctly
     * oriented pixels.
     */
    private async createRotatedBitmap(
        frame: VideoFrame,
        rotation: number
    ): Promise<ImageBitmap> {
        const rawBitmap = await createImageBitmap(frame);
        const srcW = rawBitmap.width;
        const srcH = rawBitmap.height;

        // For 90° or 270° rotation, output dimensions are swapped
        const swap = rotation === 90 || rotation === 270;
        const outW = swap ? srcH : srcW;
        const outH = swap ? srcW : srcH;

        const canvas = new OffscreenCanvas(outW, outH);
        const ctx = canvas.getContext("2d")!;

        ctx.translate(outW / 2, outH / 2);
        ctx.rotate((-rotation * Math.PI) / 180);
        ctx.drawImage(rawBitmap, -srcW / 2, -srcH / 2);
        rawBitmap.close();

        return createImageBitmap(canvas);
    }

    public async setBackgroundOptions(options: BackgroundOptions) {
        this.backgroundOptions = options;
        this.backgroundFilter?.setBackgroundOptions(options);
        this.report({
            variation: "Update",
        });
    }

    public setVideoBGReadable(stream: ReadableStream) {
        this.backgroundFilter?.setVideoBGReadable(stream);
    }

    public async setVirtualBGImage(image: ImageBitmap) {
        await this.backgroundFilter?.setVirtualBGImage(image);
    }

    public async profile(duration: number): Promise<ResolvedWebglQuery[]> {
        return this.backgroundFilter?.profile(duration) ?? [];
    }

    public getTransformerType(): string {
        return this.backgroundOptions?.transformerType ?? "Undefined";
    }

    public enable() {
        this.isEnabled = true;
    }

    public disable() {
        this.isEnabled = false;
    }

    private report(report: Partial<Report>) {
        this.reporter.send({
            version,
            action: "MlTransformers",
            transformerType: this.backgroundOptions?.transformerType,
            config: JSON.stringify(this.backgroundOptions),
            ...report,
        });
    }
}
